/**
 * CategoryNameModal — add or rename a category on a drag-and-drop board
 * (Documents, SMS Templates, Email Templates), laid out like the Products
 * board's category modal: one name field, required and unique among the
 * account's live categories (case-insensitive). The page does the API call.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { getErrorMessage } from '@/services/apiClient';
import { DialogField, DialogFooterLayout, DialogHeading, fieldClasses, type FieldState } from './DialogChrome';

export interface CategoryNameModalProps {
  /** The category's current name; null to add one. */
  current: { id: number; name: string } | null;
  /** The account's live categories (the duplicate check skips `current`). */
  categories: { id: number; name: string }[];
  /** e.g. "SMS template" — for the description. */
  recordNoun: string;
  placeholder?: string;
  onSave: (name: string) => Promise<void>;
  onClose: () => void;
}

export function CategoryNameModal({ current, categories, recordNoun, placeholder = 'Category name', onSave, onClose }: CategoryNameModalProps) {
  const isNew = current === null;
  const [name, setName] = useState(current?.name ?? '');
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const trimmed = name.trim();
  const blank = trimmed === '';
  const taken = !blank && categories.some((c) => c.id !== current?.id && c.name.trim().toLowerCase() === trimmed.toLowerCase());
  const state: FieldState = blank || taken || error ? 'error' : 'none';
  const errors = blank ? ['Category name is required'] : taken ? ['Category name must be unique'] : [];

  async function handleSave() {
    if (blank || taken || saving) return;
    setSaving(true);
    try {
      await onSave(trimmed);
    } catch (err) {
      setError(getErrorMessage(err, 'The category could not be saved.'));
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={
        <DialogHeading
          title={isNew ? 'Add Category' : 'Edit Category'}
          variant={isNew ? 'default' : 'edit'}
          description={isNew ? `Add a category for ${recordNoun}s.` : 'Edit the category name.'}
        />
      }
      footer={
        <DialogFooterLayout errors={errors} isCreating={isNew}>
          <div className="flex-1" />
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} loading={saving} disabled={blank || taken}>
            {isNew ? 'Add' : 'Update'}
          </Button>
        </DialogFooterLayout>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
        <DialogField
          label="Category Name"
          htmlFor="category-name"
          required
          state={state}
          labelHint={blank ? '— required' : taken ? '— name already used' : undefined}
        >
          <Input
            id="category-name"
            autoFocus
            placeholder={placeholder}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(undefined);
            }}
            error={error}
            className={fieldClasses.input(state)}
          />
        </DialogField>
      </form>
    </Modal>
  );
}
